import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {
  BooleanField, CreateButton, Datagrid, DateField, FilterButton, FunctionField, List, SelectArrayInput, SelectInput, TextField,
  TopToolbar, useGetList,
} from 'react-admin';
import ListPagination from '../../components/ListPagination';
import { __t } from '../../i18n/t';
import ManualLinkButton from '../../manual/components/ManualLinkButton';
import { DevRequest, DevRequestLane, DevRequestWorker } from '../../models/devRequest';
import { DATE_TIME } from '../attendantGoalOffers/labels';
import { laneState, LaneState } from './actions';
import InternalGate from './InternalGate';
import {
  agoLabel, clockLabel, laneCadence, laneLabel, laneStateLabel, requestStatus, statusChoices, typeChoices, typeLabel,
} from './labels';

const buildFilters = () => [
  <SelectArrayInput key="status" source="status" label="Status" choices={statusChoices()} sx={{ minWidth: 260 }} alwaysOn />,
  <SelectInput key="type" source="type" label="Tipo" choices={typeChoices()} sx={{ minWidth: 180 }} alwaysOn />,
  <SelectInput
    key="urgent"
    source="urgent"
    label="Urgente"
    choices={[{ id: true, name: __t('Sim') }, { id: false, name: __t('Não') }]}
    sx={{ minWidth: 140 }}
    alwaysOn
  />,
];

const dateTime = { showTime: true, locales: 'pt-BR', options: DATE_TIME, emptyText: '—', sx: { whiteSpace: 'nowrap' } } as const;

const LANES = [DevRequestLane.urgent, DevRequestLane.resume, DevRequestLane.normal];

const STATE_COLOR: Record<LaneState, string> = { ok: 'success.main', busy: 'info.main', silent: 'error.main' };

/** Uma fila do robô: se está viva, quando verificou pela última vez e de quanto em quanto tempo verifica. */
const LaneCard = ({ lane, worker, now }: { lane: DevRequestLane; worker?: DevRequestWorker; now: number }) => {
  const state = laneState(lane, worker, now);
  const color = STATE_COLOR[state];
  const detail = state === 'busy' && worker?.busy
    ? __t('Trabalhando no pedido #%{n} desde %{hora}', { n: worker.busy.number, hora: clockLabel(worker.busy.since, now) })
    : worker?.lastSeenAt
      ? __t('Última verificação às %{hora} (%{ago})', { hora: clockLabel(worker.lastSeenAt, now), ago: agoLabel(worker.lastSeenAt, now) })
      : __t('Ainda não rodou');
  return (
    <Paper
      variant="outlined"
      title={state === 'silent' ? __t('O robô roda no Mac do desenvolvedor: Mac desligado ou dormindo para a fila.') : undefined}
      sx={{ px: 1.5, py: 1, width: 280, borderLeft: 4, borderLeftColor: color }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2">{laneLabel(lane)}</Typography>
        <Chip size="small" variant="outlined" label={laneStateLabel(state)} sx={{ color, borderColor: color }} />
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5 }}>{detail}</Typography>
      <Typography variant="caption" color="text.secondary">{laneCadence(lane)}</Typography>
    </Paper>
  );
};

/** Filas do worker. Vermelho quando a urgente cala por mais de 5 min, a normal por mais de 2 h, ou um pedido em andamento para de dar sinal. */
const WorkerStatusLine = () => {
  const { data } = useGetList<DevRequestWorker>(
    'dev-requests/worker-status',
    { pagination: { page: 1, perPage: 10 } },
    { refetchInterval: 30_000 },
  );
  const now = Date.now();
  const byLane = new Map((data ?? []).map(w => [w.lane, w]));
  return (
    <Box sx={{ mb: 1.5, px: 1 }}>
      <Typography variant="overline" color="text.secondary">{__t('Filas do robô')}</Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        {LANES.map(lane => <LaneCard key={lane} lane={lane} worker={byLane.get(lane)} now={now} />)}
      </Stack>
    </Box>
  );
};

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ManualLinkButton slug="pedidos-ao-dev" label="Manual" />
  </TopToolbar>
);

const DevRequestList = () => (
  <InternalGate>
    <List
      title="Pedidos ao dev"
      perPage={25}
      pagination={<ListPagination />}
      exporter={false}
      empty={false}
      actions={<ListActions />}
      filters={buildFilters()}
      sort={{ field: 'createdAt', order: 'DESC' }}
    >
      <>
        <WorkerStatusLine />
        <Box sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
          <Datagrid bulkActionButtons={false} rowClick="show">
            <TextField source="number" label="Nº" />
            <TextField source="title" label="Título" sortable={false} />
            <FunctionField<DevRequest> source="type" label="Tipo" render={r => typeLabel(r.type)} />
            <BooleanField source="urgent" label="Urgente" />
            <FunctionField<DevRequest>
              source="status"
              label="Status"
              render={r => <Chip size="small" color={requestStatus(r).color} label={requestStatus(r).label} />}
            />
            <FunctionField<DevRequest>
              label="Projetos"
              sortable={false}
              render={r => (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {(r.projects ?? []).map(p => <Chip key={p.repo} size="small" variant="outlined" label={p.repo} />)}
                </Stack>
              )}
            />
            <FunctionField<DevRequest>
              label="App"
              sortable={false}
              render={r => (r.requiresAppUpdate ? <PhoneIphoneIcon fontSize="small" titleAccess={__t('Precisa atualizar o app')} /> : null)}
            />
            <DateField source="createdAt" label="Criado em" {...dateTime} />
          </Datagrid>
        </Box>
      </>
    </List>
  </InternalGate>
);

export default DevRequestList;
